import { atom } from 'jotai';

// Unique identifiers for each debug window
// Includes static IDs for legacy tools and dynamic IDs for tabbed panels
export type DebugWindowId =
  | 'device-inspector'
  | 'app-inspector'
  | 'misconfigured-overlay'
  | 'display-template-preview'
  | 'device-ux-editor'
  | 'debug-tools-help'
  | `dev-tools-panel-${string}`;

// Base z-index for debug windows (z-modal + 1)
export const DEBUG_WINDOW_BASE_Z_INDEX = 41;

// Track the order of windows (last item is on top)
export const debugWindowOrderAtom = atom<DebugWindowId[]>([]);

// Atom to bring a window to the front
export const bringToFrontAtom = atom(null, (get, set, windowId: DebugWindowId) => {
  const currentOrder = get(debugWindowOrderAtom);

  // Remove the window from its current position and add it to the end (top)
  const newOrder = currentOrder.filter((id) => id !== windowId);
  newOrder.push(windowId);

  set(debugWindowOrderAtom, newOrder);
});

// Atom to register a window (add to order if not present)
export const registerWindowAtom = atom(null, (get, set, windowId: DebugWindowId) => {
  const currentOrder = get(debugWindowOrderAtom);

  if (!currentOrder.includes(windowId)) {
    set(debugWindowOrderAtom, [...currentOrder, windowId]);
  }
});

// Atom to unregister a window (remove from order)
export const unregisterWindowAtom = atom(null, (get, set, windowId: DebugWindowId) => {
  const currentOrder = get(debugWindowOrderAtom);
  set(
    debugWindowOrderAtom,
    currentOrder.filter((id) => id !== windowId)
  );
});

// Selector to get the z-index for a specific window
export const getWindowZIndexAtom = atom((get) => {
  const order = get(debugWindowOrderAtom);
  return (windowId: DebugWindowId): number => {
    const index = order.indexOf(windowId);
    if (index === -1) {
      return DEBUG_WINDOW_BASE_Z_INDEX;
    }
    return DEBUG_WINDOW_BASE_Z_INDEX + index;
  };
});

// Atom to get the topmost window ID
export const getTopmostWindowAtom = atom((get) => {
  const order = get(debugWindowOrderAtom);
  return order.length > 0 ? order[order.length - 1] : null;
});

// Atom to close all debug windows
export const closeAllWindowsAtom = atom(null);
